import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Message sent successfully!", {
          description: "We'll get back to you within 24 hours."
        });
        setFormData({ name: "", phone: "", email: "", message: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message", {
        description: "Please try again or call us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Page Header */}
      <section className="bg-[#F9FAFB] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
              Contact Us
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
              Get Your Free Estimate Today
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Ready to transform your outdoor space? Contact J Guzman Landscaping for a free, 
              no-obligation estimate. We respond to all inquiries within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1F2937] mb-8 font-['Manrope']">
                Contact Information
              </h2>

              <div className="space-y-6 mb-10">
                <a 
                  href="tel:8159098790" 
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-[#F9FAFB] transition-colors group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-[#F0FDF4] rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#2F5930] transition-colors">
                    <Phone className="w-5 h-5 text-[#2F5930] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2937] mb-1">Phone</h3>
                    <p className="text-[#2F5930] font-bold text-lg">(815) 909-8790</p>
                    <p className="text-sm text-[#9CA3AF]">Click to call</p>
                  </div>
                </a>

                <a 
                  href="mailto:Jguzmanlandscaping01@gmail.com" 
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-[#F9FAFB] transition-colors group"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-[#F0FDF4] rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#2F5930] transition-colors">
                    <Mail className="w-5 h-5 text-[#2F5930] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2937] mb-1">Email</h3>
                    <p className="text-[#2F5930] font-medium">Jguzmanlandscaping01@gmail.com</p>
                    <p className="text-sm text-[#9CA3AF]">We respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4">
                  <div className="w-12 h-12 bg-[#F0FDF4] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#2F5930]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2937] mb-1">Service Area</h3>
                    <p className="text-[#4B5563]">Joliet, IL and surrounding areas</p>
                    <p className="text-sm text-[#9CA3AF]">Will County, DuPage County, Cook County</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4">
                  <div className="w-12 h-12 bg-[#F0FDF4] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#2F5930]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2937] mb-1">Business Hours</h3>
                    <p className="text-[#4B5563]">Monday - Saturday: 7AM - 5PM</p>
                    <p className="text-[#4B5563]">Sunday: Closed</p>
                    <p className="text-sm text-[#9CA3AF]">Emergency snow removal available 24/7</p>
                  </div>
                </div>
              </div>

              {/* Why Contact Us */}
              <div className="bg-[#F9FAFB] p-6 rounded-2xl">
                <h3 className="font-semibold text-[#1F2937] mb-4 font-['Manrope']">
                  What to Expect
                </h3>
                <ul className="space-y-3">
                  {[
                    "Free, no-obligation estimate",
                    "Response within 24 hours",
                    "Professional consultation",
                    "Transparent pricing"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-[#4B5563]">
                      <CheckCircle2 className="w-4 h-4 text-[#2F5930]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-bold text-[#1F2937] mb-2 font-['Manrope']">
                  Request a Free Estimate
                </h2>
                <p className="text-[#4B5563] mb-8">
                  Fill out the form below and we&apos;ll get back to you shortly.
                </p>

                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-[#2F5930]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                      Thank You!
                    </h3>
                    <p className="text-[#4B5563] mb-6">
                      Your message has been sent successfully. We&apos;ll contact you within 24 hours.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      variant="outline"
                      className="text-[#2F5930] border-[#2F5930] hover:bg-[#F0FDF4] rounded-full"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1F2937] font-medium">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="form-input w-full"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1F2937] font-medium">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="form-input w-full"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1F2937] font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="form-input w-full"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#1F2937] font-medium">
                        How Can We Help? *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project - type of service, property size, timeline, etc."
                        rows={5}
                        className="form-input w-full resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#2F5930] hover:bg-[#244525] text-white rounded-full py-6 text-lg font-semibold"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="animate-spin mr-2">&#9696;</span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-[#9CA3AF] text-center">
                      By submitting this form, you agree to be contacted regarding your inquiry.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="bg-[#2F5930] py-12" data-testid="contact-quick-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold text-white mb-2 font-['Manrope']">
                Prefer to Call?
              </h2>
              <p className="text-white/80">
                Speak directly with our team for immediate assistance.
              </p>
            </div>
            <a href="tel:8159098790">
              <Button 
                size="lg"
                className="bg-white text-[#2F5930] hover:bg-gray-100 rounded-full px-8"
                data-testid="contact-quick-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                (815) 909-8790
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
